import {
    AMARON__EVENT__CALLS__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT__CALLS__CREATED_BY_ROLE__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    AmaronEventTypes,
    AmaronLeadStatusEnum,
} from "~/domainModels/workspaceSpecific/amaron";
import {
    buildScheduleCallTimeSlotOptions,
    combineScheduleDateAndTimeSlot,
} from "~/domainModels/scheduleCall/scheduleCallShared";
import type {ScheduleCallStrategy} from "~/domainModels/scheduleCall/scheduleCallStrategy";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    buildAmaronCallJourneyActivityColumnUpdate,
    buildAmaronScheduledCallEventColumnUpdate,
    markAmaronFirstEnquiryEventEngagedOnExistingJourney,
} from "~/utilities/workspaceSpecific/amaronCallSubmit";
import {getEventContainerOrganizationalUnitId} from "~/utilities/ottopilotSemanticColumns";

function readAmaronSchedulePhoneNumber(raw: unknown): string {
    if (typeof raw === "string") {
        return raw.trim();
    }
    if (typeof raw === "number" || typeof raw === "boolean") {
        return String(raw).trim();
    }
    return "";
}

export const amaronScheduleCallStrategy: ScheduleCallStrategy = {
    callEventTemplateId: AmaronEventTypes.Calls as Uuid,
    supportsInlineModal: true,
    usesAmaronBackendOrchestration: true,
    resolveCallEventTemplate: (eventTemplates) =>
        eventTemplates.find((template) => template.id === (AmaronEventTypes.Calls as Uuid)),
    buildCallTimeSlotOptions: buildScheduleCallTimeSlotOptions,
    combineDateAndTimeSlot: combineScheduleDateAndTimeSlot,
    buildSubmitPayload: ({journey, journeyTemplate, scheduledOnIso, workspaceUserId, workspaceUserTemplateId}) => {
        const organizationalUnitId = getEventContainerOrganizationalUnitId(journey, journeyTemplate);

        const phoneRaw = journey.additionalColumnValues[AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID];
        const phoneNumber = readAmaronSchedulePhoneNumber(phoneRaw);
        if (phoneNumber === "") {
            throw new Error("Contact number is required on the lead before scheduling a call.");
        }

        const journeyLeadStatusLive = AmaronLeadStatusEnum.LIVE as Uuid;

        const journeyAdditionalColumnValues: Record<Uuid, unknown> = {
            ...journey.additionalColumnValues,
            [AMARON__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID]: phoneNumber,
            [AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID]: workspaceUserId,
            [AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID]: journeyLeadStatusLive,
            ...buildAmaronCallJourneyActivityColumnUpdate({
                journeyAdditionalColumnValues: journey.additionalColumnValues,
            }),
        };

        const cityRaw = journey.additionalColumnValues[AMARON__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID];
        const stateRaw = journey.additionalColumnValues[AMARON__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID];

        const eventAdditionalColumnValues: Record<Uuid, unknown> = {
            ...buildAmaronScheduledCallEventColumnUpdate({
                scheduledOnIso: scheduledOnIso,
                workspaceUserId: workspaceUserId,
            }),
            [AMARON__EVENT__CALLS__CREATED_BY_ROLE__COLUMN_ID]: workspaceUserTemplateId,
            [AMARON__EVENT__CALLS__CONTACT_NUMBER__COLUMN_ID]: phoneNumber,
            ...(typeof cityRaw === "string" && cityRaw.trim() !== ""
                ? {[AMARON__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID]: cityRaw}
                : {}),
            ...(typeof stateRaw === "string" && stateRaw.trim() !== ""
                ? {[AMARON__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID]: stateRaw}
                : {}),
        };

        return {
            journeyUpdate: {
                ouId: organizationalUnitId,
                additionalColumnValues: journeyAdditionalColumnValues,
            },
            eventAdditionalColumnValues,
        };
    },
    runAfterSuccess: async ({journey, accessToken, intellsysBaseUrl, workspaceId}) => {
        const markEngagedResult = await markAmaronFirstEnquiryEventEngagedOnExistingJourney({
            journey: journey,
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            workspaceId: workspaceId,
        });
        if (markEngagedResult.success !== true) {
            throw markEngagedResult.error;
        }
    },
};

export const amaronListScheduleCallStrategy = amaronScheduleCallStrategy;
